import type { CatalogFile } from '../types/catalog';
import type { PendingOp } from './CatalogStorage';

export interface SyncBackend {
  getPendingOps(): PendingOp[];
  setPendingOps(ops: PendingOp[]): void;
  getRemoteId(key: string): string | undefined;
  setRemoteId(key: string, id: string): void;
  deleteRemoteId(key: string): void;
  saveRemoteMap(): void;
  savePendingOps(): void;
  addPendingOp(op: Omit<PendingOp, 'id' | 'timestamp' | 'retries'>): void;
}

export async function syncPending(sync: SyncBackend): Promise<void> {
  const { isSupabaseConfigured } = await import('./api');
  if (!isSupabaseConfigured()) return;

  const { getCurrentUser } = await import('./api');
  const user = await getCurrentUser();
  if (!user) return;

  const ops = [...sync.getPendingOps()];
  let changed = false;

  for (const op of ops) {
    try {
      if (op.type === 'add' && op.catalog) {
        const { importCatalogToRemote } = await import('./api');
        const { catalog: remote } = await importCatalogToRemote(op.catalog);
        sync.setRemoteId(op.key, remote.id);
      } else if (op.type === 'remove') {
        const remoteId = sync.getRemoteId(op.key);
        if (remoteId) {
          const { deleteRemoteCatalog } = await import('./api');
          await deleteRemoteCatalog(remoteId);
          sync.deleteRemoteId(op.key);
        }
      }
      sync.setPendingOps(sync.getPendingOps().filter(p => p.id !== op.id));
      changed = true;
    } catch {
      op.retries++;
      if (op.retries >= 5) {
        sync.setPendingOps(sync.getPendingOps().filter(p => p.id !== op.id));
        changed = true;
      }
    }
  }

  if (changed) sync.savePendingOps();
}

export async function syncAddToRemote(
  sync: SyncBackend,
  localKey: string,
  catalog: CatalogFile,
  isFromRemote: boolean,
): Promise<void> {
  if (isFromRemote) return;

  const { isSupabaseConfigured } = await import('./api');
  if (!isSupabaseConfigured()) return;

  const { getCurrentUser } = await import('./api');
  const user = await getCurrentUser();
  if (!user) return;

  try {
const { importCatalogToRemote } = await import('./api');
    const { catalog: remote } = await importCatalogToRemote(catalog);
    sync.setRemoteId(localKey, remote.id);
  } catch {
    sync.addPendingOp({
      type: 'add',
      key: localKey,
      catalog,
    });
  }
}

export async function syncRemoveFromRemote(sync: SyncBackend, localKey: string): Promise<void> {
  const { isSupabaseConfigured } = await import('./api');
  if (!isSupabaseConfigured()) return;

  const { getCurrentUser } = await import('./api');
  const user = await getCurrentUser();
  if (!user) return;

  const remoteId = sync.getRemoteId(localKey);
  if (!remoteId) return;

  try {
const { deleteRemoteCatalog } = await import('./api');
    await deleteRemoteCatalog(remoteId);
    sync.deleteRemoteId(localKey);
  } catch {
    sync.addPendingOp({
      type: 'remove',
      key: localKey,
    });
  }
}